<template>
  <div v-if="formError" class="text-sm font-medium text-error-600 text-left mb-4">{{ formError }}</div>
  <form :validation-schema="validationSchema" @submit="sendMessage">
    <div class="flex flex-col items-start w-full text-left gap-3 sm:gap-5">
      <div class="w-full text-base-white flex flex-col items-stretch">
        <div class="mb-4 sm:mb-5">
          <FormField v-slot="{ componentField }" name="interest">
            <FormItem>
              <FormLabel class="text-sm">Select your area of interest:</FormLabel>
              <Select v-bind="componentField">
                <FormControl>
                  <SelectTrigger
                    class="w-full h-10! sm:h-11! bg-background text-sm sm:text-base data-placeholder:text-gray-dark-500 font-extralight rounded-lg"
                  >
                    <SelectValue placeholder="Select product" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent class="bg-background text-white">
                  <SelectItem value="Lay-by" class="hover:bg-gray-800 focus:bg-gray-800 focus:text-white">
                    Lay-by
                  </SelectItem>
                  <SelectItem value="BOOST" class="hover:bg-gray-800 focus:bg-gray-800 focus:text-white">
                    BOOST
                  </SelectItem>
                  <SelectItem value="General enquiry" class="hover:bg-gray-800 focus:bg-gray-800 focus:text-white">
                    General enquiry
                  </SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          </FormField>
        </div>
        <div class="mb-4 sm:mb-5">
          <FormField v-slot="{ componentField }" name="name">
            <FormItem>
              <FormLabel class="text-sm">Name & Surname</FormLabel>
              <FormControl>
                <Input
                  type="text"
                  placeholder="Enter your name & surname"
                  class="h-10 sm:h-11 text-sm sm:text-base placeholder:text-gray-dark-500 placeholder:font-extralight"
                  v-bind="componentField"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>
        </div>
        <div class="mb-4 sm:mb-5">
          <FormField v-slot="{ componentField }" name="businessName">
            <FormItem>
              <FormLabel class="text-sm">Business Name</FormLabel>
              <FormControl>
                <Input
                  type="text"
                  placeholder="Enter your business' name"
                  class="h-10 sm:h-11 text-sm sm:text-base placeholder:text-gray-dark-500 placeholder:font-extralight"
                  v-bind="componentField"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>
        </div>

        <!-- Email -->
        <div class="mb-4 sm:mb-5">
          <FormField v-slot="{ componentField }" name="email">
            <FormItem>
              <FormLabel class="text-sm">Email</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder="Enter your email address"
                  class="h-10 sm:h-11 text-sm sm:text-base placeholder:text-gray-dark-500 placeholder:font-extralight"
                  v-bind="componentField"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>
        </div>

        <!-- Phone number -->
        <div class="mb-4 sm:mb-5">
          <FormField v-slot="{ componentField }" name="phoneNumber">
            <FormItem>
              <FormLabel class="text-sm">Phone Number</FormLabel>
              <FormControl>
                <Input
                  type="tel"
                  placeholder="Enter your phone number"
                  class="h-10 sm:h-11 text-sm sm:text-base placeholder:text-gray-dark-500 placeholder:font-extralight"
                  v-bind="componentField"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>
        </div>

        <!-- Website URL -->
        <div class="mb-4 sm:mb-5">
          <FormField v-slot="{ componentField }" name="website">
            <FormItem>
              <FormLabel class="text-sm">Website</FormLabel>
              <FormControl>
                <Input
                  type="text"
                  placeholder="Enter your website URL"
                  class="h-10 sm:h-11 text-sm sm:text-base placeholder:text-gray-dark-500 placeholder:font-extralight"
                  v-bind="componentField"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>
        </div>

        <!-- Message -->
        <div class="mb-4 sm:mb-5">
          <FormField v-slot="{ componentField }" name="message">
            <FormItem>
              <FormLabel class="text-sm">Message</FormLabel>
              <FormControl>
                <Textarea
                  rows="5"
                  placeholder="Type your message here..."
                  class="text-sm sm:text-base placeholder:text-gray-dark-500 placeholder:font-extralight"
                  v-bind="componentField"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>
        </div>

        <!-- Turnstile Widget -->
        <TurnstileWidget ref="turnstileRef" @success="handleSuccess" />

        <!-- Send Message Button -->
        <Button
          type="submit"
          size="lg"
          class="w-full md:w-auto mt-4 sm:mt-6 h-11 sm:h-12 text-sm sm:text-base mx-auto"
          :disabled="isLoading"
        >
          <span v-if="isLoading">Sending...</span>
          <span v-else>Send message</span>
        </Button>
        <div v-if="successMessage" class="text-sm font-medium text-success-600 text-center my-4">
          {{ successMessage }}
        </div>
      </div>
    </div>
  </form>
</template>

<script setup lang="ts">
import { trackMetaLead } from '@/analytics/metaPixel';
import TurnstileWidget from '@/common/TurnstileWidget.vue';
import { Button } from '@/components/ui/button';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { sendEmail } from '@/services/email';
import { NetworkError } from '@/services/layupApiClient';
import { isValidPhoneNumber, parseUrl } from '@/utils/form';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import { ref } from 'vue';
import { event } from 'vue-gtag';
import * as z from 'zod';

const validationSchema = toTypedSchema(
  z.object({
    interest: z.string(),
    name: z.string().min(2, 'Name & Surname must be at least 2 characters'),
    businessName: z.string().min(2, 'Business name must be at least 2 characters'),
    email: z.string().email('Please enter a valid email address'),
    phoneNumber: z
      .string()
      .min(1, 'Phone number is required')
      .refine((value) => isValidPhoneNumber(value), 'Please enter a valid phone number'),
    website: z
      .string()
      .optional()
      .refine((url) => parseUrl(url || ''), 'Please enter a valid website URL'),
    message: z.string().min(10, 'Message must be at least 10 characters'),
  }),
);
const form = useForm({
  validationSchema: validationSchema,
});

const turnstileRef = ref<InstanceType<typeof TurnstileWidget> | null>(null);
const isLoading = ref(false);
const formError = ref('');
const captchaToken = ref('');
const successMessage = ref('');

interface FormValues {
  name: string;
  businessName: string;
  email: string;
  phoneNumber: string;
  website?: string;
  message: string;
}

const sendMessage = form.handleSubmit(async (values: FormValues) => {
  const formValues = values;
  formError.value = '';
  successMessage.value = '';

  if (!captchaToken.value) {
    formError.value = 'Please complete the captcha verification';
    return;
  }

  isLoading.value = true;

  try {
    await sendEmail({
      ...formValues,
      type: 'merchant-enquiry',
      captchaToken: captchaToken.value,
    });
    addMetric('merchant_enquiry_form', { status: 'success', channel: 'website' });
    event('generate_lead', {
      form_name: 'business_enquiry',
      method: 'website',
    });
    turnstileRef.value?.reset();
    captchaToken.value = '';
    formError.value = '';
    form.resetForm();
    successMessage.value = 'Your message has been sent successfully!';
    trackMetaLead();
  } catch (error) {
    turnstileRef.value?.reset();
    captchaToken.value = '';
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('merchant_enquiry_form', { status: 'network_error', channel: 'website' });
      formError.value = 'Network error. Please try again.';
    } else {
      createException(error);
      addMetric('merchant_enquiry_form', { status: 'error', channel: 'website' });
      formError.value = 'Failed to send message. Please try again.';
    }
  } finally {
    isLoading.value = false;
  }
});

function handleSuccess(token: string) {
  captchaToken.value = token;
}
</script>
